import React, { useState, useEffect } from 'react';
import { collection, addDoc, query, where, getDocs, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { createSolicitudEmpresaSinAuth } from '../features/auth/services/onboardingUserService';
import { useAuth } from '../features/auth/components/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { NotificationService } from '../utils/notificationService';
import { getRegiones, getComunas } from '../utils/regionesComunas';
import LogoKawesCar from './LogoKawesCar';
import { LAUNCH } from '../constants/launchMessaging';
import { SHOW_PUBLIC_LOGIN_NAV } from '../constants/publicAccess';
import { formatRut, isValidRut, isValidTelefono as isValidTelefonoEmpresa } from '../utils/rutUtils';

// Componente para registro sin autenticación
function RegistroProveedorSinAuth({ onSubmit, isAgente = false, agenteInfo = null, loading = false, searchOverride = null, isModal = false, onClose = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, rol } = useAuth();
  const [currentStep, setCurrentStep] = useState(1); // Comenzar directamente en paso 1 (Información de la Empresa)

  // Función para obtener la ruta del dashboard según el rol
  const getDashboardRoute = () => {
    if (!authUser) return '/login';
    
    if (rol === 'admin') {
      return '/admin';
    } else if (rol === 'agente') {
      return '/dashboard/agente';
    } else if (rol === 'proveedor' || rol === 'empresa') {
      return '/dashboard/proveedor';
    } else if (rol === 'mecanico') {
      return '/dashboard/mecanico';
    } else if (rol === 'cliente') {
      return '/dashboard/cliente';
    } else {
      return '/dashboard/cliente';
    }
  };
  const [formData, setFormData] = useState({
    // Información de la Empresa
    nombre_empresa: '',
    rut_empresa: '',
    direccion_empresa: '',
    comuna: '',
    ciudad: 'Santiago',
    region: 'Metropolitana',
    telefono_empresa: '',
    email_empresa: '',
    web_actual: '',
    // Clasificación de empresa
    rubro: '',
    rubroPersonalizado: '', // Para cuando se selecciona "Otros"
    tipoEmpresa: 'proveedor', // Solo proveedores en MVP
    // Información del Representante
    nombres_representante: '',
    apellidos_representante: '',
    cargo_representante: '',
    telefono_representante: '',
    email_representante: '',
    rut_representante: '',
    // Información del Negocio
    descripcion_negocio: '',
    anos_funcionamiento: '',
    numero_empleados: '',
    horario_atencion: '',
    categorias_servicios: [],
    marcas_vehiculos: [],
    // Solicitudes de nueva marca o servicio (si no existen en catálogo)
    solicitudes_marca_nueva: [],
    solicitudes_servicio_nueva: [],
    inputNuevaMarca: '',
    inputNuevoServicio: '',
    // Horarios predefinidos para edición visual
    horarios: {
      lunes: { activo: true, inicio: '09:00', fin: '18:00' },
      martes: { activo: true, inicio: '09:00', fin: '18:00' },
      miercoles: { activo: true, inicio: '09:00', fin: '18:00' },
      jueves: { activo: true, inicio: '09:00', fin: '18:00' },
      viernes: { activo: true, inicio: '09:00', fin: '18:00' },
      sabado: { activo: true, inicio: '09:00', fin: '14:00' },
      domingo: { activo: false, inicio: '10:00', fin: '14:00' }
    },
    // Servicios Web que necesita
    necesita_pagina_web: false,
    tipo_pagina_web: '', // 'basica', 'intermedia', 'completa'
    tiene_redes_sociales: false,
    redes_sociales: {
      facebook: '',
      instagram: '',
      whatsapp: '',
      tiktok: ''
    },
    // Términos y condiciones
    acepta_terminos: false,
    acepta_notificaciones: true,
    acepta_visita_campo: true,
    // Opciones de flujo comercial: directorio, dashboard gratis, ayuda web, lotes y ventas
    selectedOptions: { directory: true, freeDashboard: false, websiteHelp: false, membershipLots: false, salesContact: false },
    membershipLot: '',
    // Plan web si websiteHelp: 'basico' | 'intermedio' | 'personalizado'
    websitePlan: ''
  });
  const [showStep0, setShowStep0] = useState(false);
  const [categoriasDisponibles, setCategoriasDisponibles] = useState([]);
  const [marcasDisponibles, setMarcasDisponibles] = useState([]);
  const [servicioSearch, setServicioSearch] = useState('');
  const [marcaSearch, setMarcaSearch] = useState('');
  const [rubrosDisponibles, setRubrosDisponibles] = useState([
    'Vulcanización', 'Lavado', 'Talleres mecánicos', 'Pintura', 'Lubricentro', 'Accesorios', 'Grúas', 'Motos', 'Repuestos', 'Neumáticos', 'Baterías', 'Frenos', 'Eléctrico', 'Carrocería', 'Escapes', 'Filtros', 'Herramientas', 'Diagnóstico', 'Mantención', 'Mecánica General', 'Audio y Alarmas', 'Climatización', 'Transmisión', 'Suspensión', 'Desarmaduría', 'Otros'
  ]);
  const [regionesDisponibles, setRegionesDisponibles] = useState([]);
  const [comunasDisponibles, setComunasDisponibles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [solicitudEnviada, setSolicitudEnviada] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(30);
  const [avisoProgress, setAvisoProgress] = useState(0);
  const DURACION_AVISO_SEG = 30;

  // Aviso post-envío: progreso y redirección a HomePage en ~30 s (navigate en setTimeout para no llamarlo dentro del setState)
  useEffect(() => {
    if (!solicitudEnviada) return;
    if (isModal) {
      const t = setTimeout(() => onClose?.(), 3500);
      return () => clearTimeout(t);
    }
    setRedirectCountdown(DURACION_AVISO_SEG);
    setAvisoProgress(0);
    const intervalMs = 200;
    const totalTicks = (DURACION_AVISO_SEG * 1000) / intervalMs;
    let tick = 0;
    const progressInterval = setInterval(() => {
      tick += 1;
      setAvisoProgress(Math.min(100, (tick / totalTicks) * 100));
    }, intervalMs);
    const countdownInterval = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(progressInterval);
          clearInterval(countdownInterval);
          setTimeout(() => navigate('/', { replace: true }), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      clearInterval(progressInterval);
      clearInterval(countdownInterval);
    };
  }, [solicitudEnviada, navigate]);

  // Logo
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  // Manejo de archivo de logo
  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoFile(file);
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Determinar tipo de empresa según la ruta o query params
  useEffect(() => {
    const searchParams = new URLSearchParams(searchOverride ?? location.search);
    const tipoFromQuery = searchParams.get('tipo');
    const solutionFromQuery = searchParams.get('solucion');
    const lotFromQuery = searchParams.get('lote');
    const pathname = location.pathname;
    let tipoDetectado = '';
    
    // En MVP solo hay proveedores - siempre comenzar en paso 1
    tipoDetectado = 'proveedor';
    setCurrentStep(1); // Siempre comenzar en paso 1 (Información de la Empresa)
    setShowStep0(false);
    const wantsAgent = tipoFromQuery === 'agente-web' || solutionFromQuery === 'agente-web';
    const wantsLots = tipoFromQuery === 'lotes' || solutionFromQuery === 'lotes' || Boolean(lotFromQuery);
    const wantsSales = solutionFromQuery === 'ventas';
    const wantsDashboard = solutionFromQuery === 'dashboard';
    
    // Siempre establecer como proveedor
    setFormData(prev => ({
      ...prev,
      tipoEmpresa: 'proveedor',
      selectedOptions: {
        ...prev.selectedOptions,
        directory: true,
        freeDashboard: prev.selectedOptions?.freeDashboard || wantsDashboard,
        websiteHelp: prev.selectedOptions?.websiteHelp || wantsAgent,
        membershipLots: prev.selectedOptions?.membershipLots || wantsLots,
        salesContact: prev.selectedOptions?.salesContact || wantsSales,
      },
      necesita_pagina_web: prev.necesita_pagina_web || wantsAgent,
      tipo_pagina_web: wantsAgent && !prev.tipo_pagina_web ? 'completa' : prev.tipo_pagina_web,
      membershipLot: lotFromQuery || prev.membershipLot || '',
    }));
  }, [location.pathname, location.search]);

  // Asegurar que tipoEmpresa siempre sea 'proveedor' en MVP
  useEffect(() => {
    if (formData.tipoEmpresa !== 'proveedor') {
      setFormData(prev => ({
        ...prev,
        tipoEmpresa: 'proveedor'
      }));
    }
  }, [formData.tipoEmpresa]);

  // Cargar datos iniciales
  useEffect(() => {
    setRegionesDisponibles(getRegiones());
  }, []);

  // Actualizar comunas cuando cambie la región
  useEffect(() => {
    if (formData.region) {
      setComunasDisponibles(getComunas(formData.region));
      setFormData(prev => ({ ...prev, comuna: '' }));
    } else {
      setComunasDisponibles([]);
      setFormData(prev => ({ ...prev, comuna: '' }));
    }
  }, [formData.region]);

  // Cargar categorías de servicios (misma fuente que /admin/servicios-admin), marcas y rubros desde Firebase
  useEffect(() => {
    async function cargarDatos() {
      try {
        // Categorías de servicios = colección 'servicios' (igual que admin/servicios-admin)
        const serviciosSnap = await getDocs(collection(db, 'servicios'));
        const serviciosList = serviciosSnap.docs
          .map(doc => doc.data())
          .filter(s => s.nombre && s.activa !== false)
          .map(s => s.nombre)
          .sort((a, b) => (a || '').localeCompare(b || ''));
        setCategoriasDisponibles(serviciosList);

        // Cargar marcas (sin duplicados por nombre). Incluir "Multimarca" si no existe.
        const marcasSnap = await getDocs(collection(db, 'marcas'));
        const marcasSet = new Set(marcasSnap.docs.map(d => d.data().nombre).filter(Boolean));
        if (!marcasSet.has('Multimarca')) marcasSet.add('Multimarca');
        const marcasList = [...marcasSet];
        setMarcasDisponibles(marcasList);

        // Cargar rubros (Rubro Específico) desde admin - Gestión de Rubros (para otros usos si aplica)
        const rubrosSnap = await getDocs(collection(db, 'rubros_empresa'));
        const rubrosList = rubrosSnap.docs
          .map(doc => doc.data())
          .filter(r => r.nombre && r.activa !== false)
          .map(r => r.nombre)
          .sort((a, b) => a.localeCompare(b));
        if (rubrosList.length > 0) {
          setRubrosDisponibles(rubrosList);
        }
      } catch (error) {
        console.error('Error loading services, brands and rubros:', error);
      }
    }
    cargarDatos();
  }, []);

  // Paso 3: preseleccionar "Multimarca" en Marcas si aún no hay ninguna seleccionada (al entrar al paso 3 o al cargar marcas; no resetear si el usuario ya eligió)
  useEffect(() => {
    if (currentStep !== 3) return;
    const actual = formData.marcas_vehiculos || [];
    if (actual.length > 0) return;
    if (!marcasDisponibles || !marcasDisponibles.some(m => m === 'Multimarca')) return;
    setFormData(prev => ({
      ...prev,
      marcas_vehiculos: ['Multimarca']
    }));
  }, [currentStep, marcasDisponibles]);

  const regiones = [
    "Arica y Parinacota", "Tarapacá", "Antofagasta", "Atacama", "Coquimbo", "Valparaíso",
    "Metropolitana", "O'Higgins", "Maule", "Ñuble", "Biobío", "La Araucanía",
    "Los Ríos", "Los Lagos", "Aysén", "Magallanes"
  ];

  const opcionesPaginaWeb = [
    {
      tipo: 'basica',
      nombre: 'Página Básica',
      precio: '$299.990',
      descripcion: 'Sitio web simple con información básica de tu empresa',
      incluye: [
        'Página principal con información de contacto',
        'Galería de servicios básica',
        'Formulario de contacto',
        'Responsive (adaptable a móviles)',
        'Hosting incluido por 1 año'
      ]
    },
    {
      tipo: 'intermedia',
      nombre: 'Página Intermedia',
      precio: '$549.990',
      descripcion: 'Sitio web completo con funcionalidades avanzadas',
      incluye: [
        'Todo lo de la página básica',
        'Catálogo de productos/servicios',
        'Sistema de citas online',
        'Integración con redes sociales',
        'Chat en vivo',
        'Blog/noticias',
        'SEO básico'
      ]
    },
    {
      tipo: 'completa',
      nombre: 'Página Completa',
      precio: '$899.990',
      descripcion: 'Plataforma e-commerce completa para tu negocio',
      incluye: [
        'Todo lo de la página intermedia',
        'Tienda online completa',
        'Sistema de pagos integrado',
        'Gestión de inventario',
        'Dashboard administrativo',
        'App móvil básica',
        'SEO avanzado y marketing digital',
        'Soporte prioritario por 6 meses'
      ]
    }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'categorias_servicios') {
        setFormData(prev => ({
          ...prev,
          categorias_servicios: checked 
            ? [...prev.categorias_servicios, value]
            : prev.categorias_servicios.filter(c => c !== value)
        }));
      } else if (name === 'marcas_vehiculos') {
        setFormData(prev => ({
          ...prev,
          marcas_vehiculos: checked 
            ? [...prev.marcas_vehiculos, value]
            : prev.marcas_vehiculos.filter(m => m !== value)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else if (name === 'redes_sociales.whatsapp') {
      // Mismo formato que teléfono: +56 y 9 dígitos
      const v = value.replace(/\D/g, '').slice(0, 9);
      setFormData(prev => ({
        ...prev,
        redes_sociales: {
          ...prev.redes_sociales,
          whatsapp: v
        }
      }));
    } else if (name.startsWith('redes_sociales.')) {
      const redSocial = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        redes_sociales: {
          ...prev.redes_sociales,
          [redSocial]: value
        }
      }));
    } else if (name === 'rut_empresa' || name === 'rut_representante') {
      setFormData(prev => ({
        ...prev,
        [name]: formatRut(value)
      }));
    } else if (name === 'telefono_empresa') {
      // 9 dígitos (prefijo +56 fijo en UI; ej: 912345678)
      const v = value.replace(/\D/g, '').slice(0, 9);
      setFormData(prev => ({ ...prev, [name]: v }));
    } else if (name === 'telefono_representante') {
      // 9 dígitos (prefijo +56 fijo en UI; ej: 912345678)
      const v = value.replace(/\D/g, '').slice(0, 9);
      setFormData(prev => ({ ...prev, [name]: v }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Limpiar error cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.nombre_empresa.trim()) newErrors.nombre_empresa = 'El nombre de la empresa es obligatorio';
      if (!isModal) {
        if (!formData.rut_empresa.trim()) newErrors.rut_empresa = 'El RUT de la empresa es obligatorio';
        else if (!isValidRut(formData.rut_empresa)) newErrors.rut_empresa = 'RUT inválido. Debe tener hasta 9 dígitos y terminar en número o K';
        else {
          const rutClean = formData.rut_empresa.replace(/\./g, '').replace(/-/g, '').toUpperCase();
          const cuerpo = parseInt(rutClean.slice(0, -1), 10);
          if (isNaN(cuerpo) || cuerpo <= 50000000) {
            newErrors.rut_empresa = 'El RUT de la empresa debe ser mayor a 50.000.000';
          }
        }
      }
      if (!formData.direccion_empresa.trim()) newErrors.direccion_empresa = 'La dirección es obligatoria';
      if (!formData.region) newErrors.region = 'La región es obligatoria';
      if (!formData.comuna?.trim()) newErrors.comuna = 'La comuna es obligatoria';
      if (!formData.telefono_empresa?.trim()) newErrors.telefono_empresa = 'El teléfono de la empresa es obligatorio';
      else if (formData.telefono_empresa.length !== 9 || !/^\d{9}$/.test(formData.telefono_empresa)) newErrors.telefono_empresa = 'Ingresa los 9 dígitos del celular (ej: 912345678)';
      if (!formData.email_empresa.trim()) newErrors.email_empresa = 'El email de la empresa es obligatorio';
      else if (!/\S+@\S+\.\S+/.test(formData.email_empresa)) newErrors.email_empresa = 'El email no es válido';
      // Rubro (Giro) ya no se solicita en paso 1
    } else if (currentStep === 2) {
      if (!formData.nombres_representante.trim()) newErrors.nombres_representante = 'Los nombres del representante son obligatorios';
      if (!formData.apellidos_representante.trim()) newErrors.apellidos_representante = 'Los apellidos del representante son obligatorios';
      if (!isModal) {
        if (!formData.rut_representante || !formData.rut_representante.trim()) {
          newErrors.rut_representante = 'El RUT del representante es obligatorio';
        } else if (!isValidRut(formData.rut_representante)) {
          newErrors.rut_representante = 'RUT inválido. Debe tener hasta 9 dígitos y terminar en número o K';
        } else {
          const rutClean = formData.rut_representante.replace(/\./g, '').replace(/-/g, '').toUpperCase();
          const cuerpo = parseInt(rutClean.slice(0, -1), 10);
          if (isNaN(cuerpo) || cuerpo >= 50000000) {
            newErrors.rut_representante = 'El RUT del representante debe ser menor a 50.000.000';
          }
        }
      }
      if (!formData.email_representante.trim()) newErrors.email_representante = 'El email del representante es obligatorio';
      if (formData.email_representante && !/\S+@\S+\.\S+/.test(formData.email_representante)) {
        newErrors.email_representante = 'El email no es válido';
      }
      if (!formData.telefono_representante?.trim()) newErrors.telefono_representante = 'El teléfono del representante es obligatorio';
      else if (formData.telefono_representante.length !== 9 || !/^\d{9}$/.test(formData.telefono_representante)) {
        newErrors.telefono_representante = 'Ingresa los 9 dígitos (ej: 912345678)';
      }
    } else if (!isModal && currentStep === 3) {
      // Información del negocio
      if (!formData.descripcion_negocio.trim()) newErrors.descripcion_negocio = 'La descripción del negocio es obligatoria';
      if (!formData.anos_funcionamiento) newErrors.anos_funcionamiento = 'Los años de funcionamiento son obligatorios';
      // Validación de horario: al menos un día activo y con horas válidas
      const horarios = formData.horarios || {};
      const diasActivos = Object.values(horarios).filter(h => h.activo);
      const horarioValido = diasActivos.length > 0 && diasActivos.every(h => h.inicio && h.fin);
      if (!horarioValido) newErrors.horario_atencion = 'Debes ingresar al menos un día activo y sus horas de inicio/fin';
      if (formData.categorias_servicios.length === 0) newErrors.categorias_servicios = 'Selecciona al menos una categoría de servicio';
      if (formData.marcas_vehiculos.length === 0) newErrors.marcas_vehiculos = 'Selecciona al menos una marca de vehículo';
      
      // Si es agente y está en paso 3, también validar términos (ya que no hay paso 5)
      if (isAgente && !formData.acepta_terminos) {
        newErrors.acepta_terminos = 'Debes aceptar los Términos y Condiciones para Proveedores/Empresas';
      }
    } else if (isModal && currentStep === 3) {
      if (!formData.acepta_terminos) newErrors.acepta_terminos = 'Debes aceptar los Términos y Condiciones para continuar';
    } else if (currentStep === 4 && !isAgente) {
      // Servicios web: si eligió "ayuda con página web" en Step 0, plan web es requerido
      if (formData.selectedOptions?.websiteHelp && !formData.tipo_pagina_web) {
        newErrors.tipo_pagina_web = 'Selecciona el plan de página web que necesitas';
      }
    } else if (currentStep === 5 && !isAgente) {
      // Términos y condiciones (la contraseña la asigna el admin al aprobar) - Solo para empresas creadas por cliente
      if (!formData.acepta_terminos) newErrors.acepta_terminos = 'Debes aceptar los Términos y Condiciones para Proveedores/Empresas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showWebsiteStep = !isModal && !isAgente && formData.selectedOptions?.websiteHelp;
  const maxStep = isModal ? 3 : isAgente ? 3 : (showWebsiteStep ? 5 : 4);
  const stepDisplay = isModal ? currentStep : (currentStep === 5 && !showWebsiteStep) ? 4 : currentStep;

  const handleNextStep = () => {
    const valid = validateStep();
    if (valid) {
      if (currentStep < maxStep) {
        if (currentStep === 3 && !isAgente && !showWebsiteStep) {
          setCurrentStep(5);
        } else {
          setCurrentStep(prev => prev + 1);
        }
      }
    } else {
      // Debug: mostrar errores en consola
      console.log('Errores de validación:', errors);
      // Forzar scroll al primer error visible
      setTimeout(() => {
        const errorElem = document.querySelector('.text-red-500');
        if (errorElem) {
          errorElem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      // Forzar actualización de estado para mostrar errores
      setErrors(e => ({ ...e }));
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      if (currentStep === 5 && !isAgente && !showWebsiteStep) {
        setCurrentStep(3);
      } else {
        setCurrentStep(prev => prev - 1);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) {
      return;
    }
    
    // Si se proporciona onSubmit personalizado (para agentes), usarlo
    if (onSubmit && typeof onSubmit === 'function') {
      setIsSubmitting(true);
      try {
        // Incluir logoPreview en formData si existe
        const formDataCompleto = {
          ...formData,
          logoPreview: logoPreview || null
        };
        await onSubmit(formDataCompleto);
      } catch (error) {
        console.error('Error en onSubmit personalizado:', error);
        alert(error.message || 'Error al procesar el formulario');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    
    // Registro sin contraseña: el agente asigna credenciales al activar
    setIsSubmitting(true);
    try {
      // Subir logo a Storage y obtener URL persistente (para que admin/agente vean logo en solicitudes)
      let logoUrlFinal = null;
      if (logoFile && typeof logoFile === 'object') {
        try {
          const path = `logos/solicitudes_empresa/${Date.now()}-${logoFile.name}`;
          const storageRef = ref(storage, path);
          await uploadBytes(storageRef, logoFile, { contentType: logoFile.type || 'image/jpeg' });
          logoUrlFinal = await getDownloadURL(storageRef);
        } catch (uploadErr) {
          console.warn('Error subiendo logo a Storage, se guardará solo preview en datos_solicitud:', uploadErr);
        }
      }
      const logoFileDataFirestore = (logoPreview && typeof logoPreview === 'string') ? logoPreview : null;
      const rs = formData.redes_sociales || {};
      const wa = String(rs.whatsapp || '').replace(/\D/g, '');
      const redesSocialesPlain = {
        facebook: String(rs.facebook ?? ''),
        instagram: String(rs.instagram ?? ''),
        whatsapp: wa.length === 9 ? '+56' + wa : String(rs.whatsapp ?? ''),
        tiktok: String(rs.tiktok ?? '')
      };
      const now = Date.now();
      const solicitudData = {
        nombre: String(formData.nombre_empresa ?? ''),
        rut: String(formData.rut_empresa ?? ''),
        direccion: String(formData.direccion_empresa ?? ''),
        comuna: String(formData.comuna ?? ''),
        ciudad: String(formData.ciudad ?? ''),
        region: String(formData.region ?? ''),
        telefono: formData.telefono_empresa && formData.telefono_empresa.length === 9 ? '+56' + formData.telefono_empresa : String(formData.telefono_empresa ?? ''),
        email: String(formData.email_empresa ?? ''),
        web: String(formData.web_actual ?? ''),
        categoria: String(formData.rubro === 'Otros' ? (formData.rubroPersonalizado ?? '') : (formData.rubro ?? '')),
        tipoEmpresa: String(formData.tipoEmpresa ?? ''),
        destacado: false,
        perfilCompleto: false,
        etapa_proceso: 'revision_inicial',
        fecha_registro: now,
        createdAt: now,
        hasSuggestions: false,
        hasBestMatch: false,
        fecha_actualizacion: now,
        fecha_ultima_actualizacion: now,
        fecha_activacion: null,
        comentario_admin: '',
        admin_activador: null,
        admin_responsable: 'admin@av10dejulio.cl',
        contactoAdicional: '',
        solicitud_origen_id: null,
        origen: 'cliente',
        representante: {
          nombre: String(formData.nombres_representante ?? ''),
          apellidos: String(formData.apellidos_representante ?? ''),
          cargo: String(formData.cargo_representante ?? ''),
          email: String(formData.email_representante ?? ''),
          telefono: formData.telefono_representante && formData.telefono_representante.length === 9 ? '+56' + formData.telefono_representante : String(formData.telefono_representante ?? ''),
          rut_representante: String(formData.rut_representante ?? '')
        },
        selectedOptions: formData.selectedOptions || { directory: true, freeDashboard: false, websiteHelp: false, membershipLots: false, salesContact: false },
        membershipLot: formData.selectedOptions?.membershipLots ? String(formData.membershipLot || '') : '',
        websitePlan: formData.selectedOptions?.websiteHelp && formData.tipo_pagina_web
          ? (formData.tipo_pagina_web === 'basica' ? 'basico' : formData.tipo_pagina_web === 'intermedia' ? 'intermedio' : formData.tipo_pagina_web === 'completa' ? 'personalizado' : formData.tipo_pagina_web)
          : '',
        datos_solicitud: {
          acepta_terminos: Boolean(formData.acepta_terminos),
          acepta_notificaciones: Boolean(formData.acepta_notificaciones),
          acepta_visita_agente: Boolean(formData.acepta_visita_campo),
          necesita_pagina_web: Boolean(formData.necesita_pagina_web || formData.selectedOptions?.websiteHelp),
          tipo_pagina_web: String(formData.tipo_pagina_web ?? ''),
          cotiza_lotes_membresias: Boolean(formData.selectedOptions?.membershipLots),
          lote_membresias: formData.selectedOptions?.membershipLots ? String(formData.membershipLot || '') : '',
          solicitud_comercial: Boolean(formData.selectedOptions?.salesContact),
          tiene_redes_sociales: Boolean(formData.tiene_redes_sociales),
          anos_funcionamiento: String(formData.anos_funcionamiento ?? ''),
          numero_empleados: String(formData.numero_empleados ?? ''),
          redes_sociales: redesSocialesPlain,
          descripcion: String(formData.descripcion_negocio ?? ''),
          descripcionCompleta: String(formData.descripcion_negocio ?? ''),
          galeria_files_data: [],
          logo_file_data: logoFileDataFirestore,
          rut_representante: String(formData.rut_representante ?? ''),
          rubro: String(formData.rubro === 'Otros' ? (formData.rubroPersonalizado ?? '') : (formData.rubro ?? '')),
          solicitudes_marca_nueva: Array.isArray(formData.solicitudes_marca_nueva) ? formData.solicitudes_marca_nueva.map(String) : [],
          solicitudes_servicio_nueva: Array.isArray(formData.solicitudes_servicio_nueva) ? formData.solicitudes_servicio_nueva.map(String) : []
        },
        servicios: Array.isArray(formData.categorias_servicios) ? formData.categorias_servicios : [],
        marcas: Array.isArray(formData.marcas_vehiculos) ? formData.marcas_vehiculos : [],
        solicitudes_marca_nueva: Array.isArray(formData.solicitudes_marca_nueva) ? formData.solicitudes_marca_nueva.map(String) : [],
        solicitudes_servicio_nueva: Array.isArray(formData.solicitudes_servicio_nueva) ? formData.solicitudes_servicio_nueva.map(String) : [],
        horarios: formData.horarios && typeof formData.horarios === 'object' ? formData.horarios : {
          lunes: { activo: true, inicio: '09:00', fin: '18:00' },
          martes: { activo: true, inicio: '09:00', fin: '18:00' },
          miercoles: { activo: true, inicio: '09:00', fin: '18:00' },
          jueves: { activo: true, inicio: '09:00', fin: '18:00' },
          viernes: { activo: true, inicio: '09:00', fin: '18:00' },
          sabado: { activo: false, inicio: '09:00', fin: '13:00' },
          domingo: { activo: false, inicio: '10:00', fin: '14:00' }
        },
        horario_atencion: (() => {
          const h = formData.horarios;
          if (!h || typeof h !== 'object') return '';
          const dias = { lunes: 'Lun', martes: 'Mar', miercoles: 'Mié', jueves: 'Jue', viernes: 'Vie', sabado: 'Sáb', domingo: 'Dom' };
          return Object.entries(h).map(([dia, c]) => (c && (c.activo || c.abierto)) ? `${dias[dia] || dia} ${c.inicio || c.apertura || ''}-${c.fin || c.cierre || ''}` : null).filter(Boolean).join(', ') || '';
        })(),
        imagenLocal: '',
        logo: logoUrlFinal || '',
        logo_url: logoUrlFinal || '',
        logoUrl: logoUrlFinal || '',
        logoAsignado: !!logoUrlFinal,
        tiene_credenciales_asignadas: false,
        usuario_empresa: null,
        webValidada: false,
        galeria: [],
        metadatos: {
          ip_registro: String(window.location.hostname || ''),
          user_agent: String(navigator.userAgent || ''),
          referencia: 'web_directa',
          tipo_registro: 'email_password',
          timestamp: new Date().toISOString()
        },
        etapas_proceso: {
          revision_inicial: { estado: 'pendiente', fecha_inicio: now, fecha_fin: null, comentarios: '', responsable: null },
          validacion_documentos: { estado: 'pendiente', fecha_inicio: null, fecha_fin: null, comentarios: '', responsable: null },
          verificacion_ubicacion: { estado: 'pendiente', fecha_inicio: null, fecha_fin: null, comentarios: '', responsable: null },
          visita_campo: { estado: 'pendiente', fecha_inicio: null, fecha_fin: null, fecha_programada: null, agente_asignado: null, comentarios: '', responsable: null },
          aprobacion_final: { estado: 'pendiente', fecha_inicio: null, fecha_fin: null, comentarios: '', responsable: null }
        },
        termsAcceptedEmpresas: true,
        termsAcceptedEmpresasAt: serverTimestamp(),
        termsVersionEmpresas: 'KWC-TC-EMPRESAS-v1.0'
      };

      const result = await createSolicitudEmpresaSinAuth(formData, solicitudData);
      if (!result.success) {
        alert(result.error || 'Error al enviar la solicitud. Intenta de nuevo.');
        setIsSubmitting(false);
        return;
      }

      await addDoc(collection(db, 'terms_acceptances'), {
        type: 'empresas',
        version: 'KWC-TC-EMPRESAS-v1.0',
        uid: null,
        companyId: null,
        solicitudId: result.solicitudId,
        acceptedAt: serverTimestamp(),
        route: '/registro-proveedor'
      });

      NotificationService.createInAppNotification(
        'admin',
        'validacion',
        'Nueva Solicitud de Proveedor',
        `${formData.nombre_empresa} (${formData.nombres_representante} ${formData.apellidos_representante}) ha enviado una solicitud de registro`,
        {
          solicitudId: result.solicitudId,
          empresaNombre: formData.nombre_empresa,
          representante: `${formData.nombres_representante} ${formData.apellidos_representante}`,
          email: formData.email_empresa,
          necesitaWeb: formData.necesita_pagina_web,
          tipoWeb: formData.tipo_pagina_web,
          etapaActual: 'revision_inicial',
          origen: 'registro_proveedor'
        }
      ).catch((err) => console.warn('Notificación admin no enviada:', err));

      setSolicitudEnviada(true);
    } catch (error) {
      console.error('Error creating provider request:', error);
      alert('Error al enviar la solicitud. Por favor, intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (solicitudEnviada && isModal) {
    return (
      <div className="px-6 py-8 text-center">
        <div className="text-5xl mb-3">✅</div>
        <h2 className="text-xl font-bold text-green-800 mb-2">Solicitud enviada</h2>
        <p className="text-gray-600 text-sm mb-1">{LAUNCH.exitoEmpresaCuerpo}</p>
        <p className="text-sm text-gray-500">Te contactaremos a <strong>{formData.email_empresa}</strong></p>
      </div>
    );
  }

  if (solicitudEnviada) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-b from-green-50 to-white px-4 py-8 overflow-y-auto">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-green-200 p-8 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-green-800 mb-2">
            Solicitud enviada correctamente
          </h2>
          <p className="text-gray-600 mb-2">
            {LAUNCH.exitoEmpresaCuerpo}
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Correo de contacto registrado: <strong>{formData.email_empresa}</strong>
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-4 text-left">
            <h3 className="font-bold text-blue-900 mb-3">Qué pasa a continuación</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li className="flex items-start gap-2"><span className="text-blue-600 font-bold">1.</span> Un agente revisará la información de tu empresa</li>
              <li className="flex items-start gap-2"><span className="text-blue-600 font-bold">2.</span> Se pondrá en contacto contigo para validar datos y orientarte</li>
              <li className="flex items-start gap-2"><span className="text-blue-600 font-bold">3.</span> Te explicará los siguientes pasos según el tipo de solución</li>
              {formData.necesita_pagina_web && (
                <li className="flex items-start gap-2"><span className="text-blue-600 font-bold">4.</span> Si incluye página web o implementación digital, el flujo se gestiona con Digital Bandurria</li>
              )}
            </ul>
          </div>

          <p className="text-xs text-gray-500 mb-6">
            El contacto puede venir desde KawésCar o Digital Bandurria según el tipo de solución solicitada.
          </p>

          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Redirigiendo al inicio</span>
              <span>{redirectCountdown} s</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-600 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${avisoProgress}%` }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate('/', { replace: true })}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-xl hover:bg-green-700 transition-colors"
          >
            {LAUNCH.btnEntendido}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={isModal ? '' : 'min-h-screen bg-gray-50'}>
      {/* Header con Logo - Solo en modo página completa */}
      {!isModal && (
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => navigate('/')}
              className="hover:opacity-80 transition-opacity"
            >
              <LogoKawesCar size="default" showText={true} />
            </button>
            <nav className="hidden md:flex items-center gap-6">
              <a href="/area-cliente" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">Área Cliente</a>
              <a href="/proveedores" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">Empresas</a>
              <a href="/quienes-somos" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">Quiénes Somos</a>
              <a href="/contacto" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">Contacto</a>
              {authUser ? (
                <button onClick={() => navigate(getDashboardRoute())} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">{LAUNCH.ctaMiCuenta}</button>
              ) : (
                SHOW_PUBLIC_LOGIN_NAV && (
                  <button type="button" onClick={() => navigate('/login')} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">Iniciar Sesión</button>
                )
              )}
            </nav>
            {(authUser || SHOW_PUBLIC_LOGIN_NAV) && (
              <button type="button" onClick={() => navigate(authUser ? getDashboardRoute() : '/login')} className="md:hidden bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">
                {authUser ? LAUNCH.ctaMiCuenta : 'Entrar'}
              </button>
            )}
          </div>
        </div>
      </header>
      )}

      <div className={isModal ? 'py-2 px-1' : 'max-w-4xl mx-auto py-8 px-4'}>
      
      {!isModal && (
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {LAUNCH.formEmpresaTitulo}
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto mb-4">
          {LAUNCH.formEmpresaSubtitulo}
        </p>
        <div className="inline-flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-2xl px-5 py-3 max-w-xl text-left">
          <span className="text-blue-600 font-black text-sm shrink-0 mt-0.5">!</span>
          <p className="text-sm text-blue-800">
            <strong>Al enviar este formulario, te contactará un agente.</strong>{' '}
            Si tu requerimiento incluye página web, agente IA o implementación digital, el flujo puede ser gestionado junto a Digital Bandurria.
          </p>
        </div>
      </div>
      )}

      {/* Step 0: Pantalla inicial – 3 opciones (directorio, dashboard, web) */}
      {showStep0 && (
        <div className="space-y-8">
          <h2 className="text-2xl font-bold text-gray-900 text-center">¿Qué necesitas?</h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto">
            Elige una o más opciones. Para aparecer en el directorio público debes marcar al menos la primera. El acceso al panel de empresa se habilita de forma progresiva y lo comunicamos por correo.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Opción 1: Directorio */}
            <button
              type="button"
              onClick={() => setFormData(prev => ({
                ...prev,
                selectedOptions: { ...prev.selectedOptions, directory: !prev.selectedOptions.directory }
              }))}
              className={`text-left rounded-2xl border-2 p-6 transition-all shadow-sm hover:shadow-md ${
                formData.selectedOptions.directory ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">🏢</span>
                <span className={`text-lg font-semibold ${formData.selectedOptions.directory ? 'text-blue-900' : 'text-gray-900'}`}>
                  Ser parte de la comunidad KawesCar
                </span>
                {formData.selectedOptions.directory && <span className="ml-auto text-blue-600 font-bold">✓</span>}
              </div>
              <p className="text-sm text-gray-700 mb-4">
                Quiero que mi negocio aparezca en KawesCar para aumentar mis ventas y recibir más clientes.
              </p>
              <div className="bg-gray-100 rounded-lg p-3 text-xs text-gray-700 space-y-1">
                <p className="font-semibold">¿Puedo ser parte del directorio si no tengo página web?</p>
                <p>Sí. Basta con que tengas un lugar físico donde atiendas y que tu negocio sea legal.</p>
              </div>
            </button>
            {/* Opción 2: Dashboard */}
            <button
              type="button"
              onClick={() => {
                setFormData(prev => {
                  const next = !prev.selectedOptions.freeDashboard;
                  return {
                    ...prev,
                    selectedOptions: {
                      ...prev.selectedOptions,
                      freeDashboard: next,
                      directory: next ? true : prev.selectedOptions.directory
                    }
                  };
                });
              }}
              className={`text-left rounded-2xl border-2 p-6 transition-all shadow-sm hover:shadow-md ${
                formData.selectedOptions.freeDashboard ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">📊</span>
                <span className={`text-lg font-semibold ${formData.selectedOptions.freeDashboard ? 'text-blue-900' : 'text-gray-900'}`}>
                  Solicito acceso al panel de empresa
                </span>
                {formData.selectedOptions.freeDashboard && <span className="ml-auto text-blue-600 font-bold">✓</span>}
              </div>
              <p className="text-sm text-gray-700">
                Envías tu solicitud; la activación es progresiva y recibirás avisos por correo cuando corresponda.
              </p>
            </button>
            {/* Opción 3: Web */}
            <button
              type="button"
              onClick={() => {
                setFormData(prev => {
                  const next = !prev.selectedOptions.websiteHelp;
                  return {
                    ...prev,
                    selectedOptions: {
                      ...prev.selectedOptions,
                      websiteHelp: next,
                      directory: next ? true : prev.selectedOptions.directory
                    },
                    necesita_pagina_web: next || prev.necesita_pagina_web
                  };
                });
              }}
              className={`text-left rounded-2xl border-2 p-6 transition-all shadow-sm hover:shadow-md ${
                formData.selectedOptions.websiteHelp ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-white'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">🌐</span>
                <span className={`text-lg font-semibold ${formData.selectedOptions.websiteHelp ? 'text-blue-900' : 'text-gray-900'}`}>
                  Quiero ayuda con una página web
                </span>
                {formData.selectedOptions.websiteHelp && <span className="ml-auto text-blue-600 font-bold">✓</span>}
              </div>
              <p className="text-sm text-gray-700">
                Quiero que KawesCar me ayude a crear una web profesional para mi empresa.
              </p>
            </button>
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => {
                if (!formData.selectedOptions.directory) {
                  alert('Debes elegir al menos "Ser parte de la comunidad KawesCar" para continuar.');
                  return;
                }
                setShowStep0(false);
              }}
              disabled={!formData.selectedOptions.directory}
              className="px-8 py-4 rounded-xl font-bold text-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {!showStep0 && (
      <form onSubmit={(isModal && currentStep === 3) || (currentStep === 5) || (isAgente && currentStep === 3) ? handleSubmit : (e) => { e.preventDefault(); handleNextStep(); }}>
        {/* Tres opciones siempre visibles; opción 1 obligatoria (no se puede deseleccionar). Al seleccionar/deseleccionar se muestra más info debajo. */}
        <div className="mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Opción 1: Directorio — siempre seleccionada, no clickeable para desmarcar */}
            <div
              className="flex-1 flex items-center gap-2 rounded-xl border-2 border-blue-600 bg-blue-50 ring-2 ring-blue-200 px-4 py-3 text-left cursor-default"
              aria-label="Directorio (obligatorio)"
            >
              <span className="text-xl">🏢</span>
              <span className="text-sm font-medium flex-1 text-blue-900">Directorio</span>
              <span className="text-blue-600 font-bold">✓</span>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => {
                const next = !prev.selectedOptions.freeDashboard;
                return {
                  ...prev,
                  selectedOptions: {
                    ...prev.selectedOptions,
                    freeDashboard: next,
                    directory: true
                  }
                };
              })}
              className={`flex-1 flex items-center gap-2 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                formData.selectedOptions?.freeDashboard ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className="text-xl">📊</span>
              <span className={`text-sm font-medium flex-1 ${formData.selectedOptions?.freeDashboard ? 'text-blue-900' : 'text-gray-700'}`}>Dashboard gratis</span>
              {formData.selectedOptions?.freeDashboard && <span className="text-blue-600 font-bold">✓</span>}
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => {
                const next = !prev.selectedOptions.websiteHelp;
                return {
                  ...prev,
                  selectedOptions: {
                    ...prev.selectedOptions,
                    websiteHelp: next,
                    directory: true
                  },
                  necesita_pagina_web: next || prev.necesita_pagina_web
                };
              })}
              className={`flex-1 flex items-center gap-2 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                formData.selectedOptions?.websiteHelp ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className="text-xl">🌐</span>
              <span className={`text-sm font-medium flex-1 ${formData.selectedOptions?.websiteHelp ? 'text-blue-900' : 'text-gray-700'}`}>Ayuda web</span>
              {formData.selectedOptions?.websiteHelp && <span className="text-blue-600 font-bold">✓</span>}
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => {
                const next = !prev.selectedOptions.membershipLots;
                return {
                  ...prev,
                  selectedOptions: {
                    ...prev.selectedOptions,
                    membershipLots: next,
                    directory: true
                  },
                  membershipLot: next ? prev.membershipLot : ''
                };
              })}
              className={`flex-1 flex items-center gap-2 rounded-xl border-2 px-4 py-3 text-left transition-all ${
                formData.selectedOptions?.membershipLots ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className="text-xl">VIP</span>
              <span className={`text-sm font-medium flex-1 ${formData.selectedOptions?.membershipLots ? 'text-blue-900' : 'text-gray-700'}`}>Lotes Premium</span>
              {formData.selectedOptions?.membershipLots && <span className="text-blue-600 font-bold">✓</span>}
            </button>
          </div>
          {/* Información expandida según opciones seleccionadas */}
          {!isModal && <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4 text-sm">
            {/* Directorio: siempre visible (obligatorio) */}
            <div>
              <p className="font-medium text-gray-900 mb-1">🏢 Ser parte del directorio</p>
              <p className="text-gray-700">Quiero que mi negocio aparezca en KawesCar para aumentar mis ventas y recibir más clientes.</p>
              <div className="mt-2 bg-white rounded-lg p-2 text-gray-600">
                <p className="font-medium text-gray-800">¿Puedo ser parte del directorio si no tengo página web?</p>
                <p>Sí. Basta con que tengas un lugar físico donde atiendas y que tu negocio sea legal.</p>
              </div>
            </div>
            {formData.selectedOptions?.freeDashboard && (
              <div className="pt-3 border-t border-gray-200">
                <p className="font-medium text-gray-900 mb-1">📊 Dashboard gratis</p>
                <p className="text-gray-700">Quiero acceso a información y métricas para tomar mejores decisiones.</p>
              </div>
            )}
            {formData.selectedOptions?.websiteHelp && (
              <div className="pt-3 border-t border-gray-200">
                <p className="font-medium text-gray-900 mb-1">🌐 Ayuda con página web</p>
                <p className="text-gray-700">Quiero que KawesCar me ayude a crear una web profesional para mi empresa.</p>
              </div>
            )}
            {formData.selectedOptions?.membershipLots && (
              <div className="pt-3 border-t border-gray-200">
                <p className="font-medium text-gray-900 mb-1">Lotes Premium Conductor</p>
                <p className="text-gray-700">Quiero cotizar membresías Premium Conductor para entregarlas como beneficio a mis clientes.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['5', '10', '20'].map((qty) => (
                    <button
                      key={qty}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, membershipLot: qty }))}
                      className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${
                        formData.membershipLot === qty
                          ? 'border-blue-600 bg-blue-600 text-white'
                          : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50'
                      }`}
                    >
                      Lote {qty}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {formData.selectedOptions?.salesContact && (
              <div className="pt-3 border-t border-gray-200">
                <p className="font-medium text-gray-900 mb-1">Solicitud comercial</p>
                <p className="text-gray-700">Un asesor puede revisar tu caso para recomendarte directorio, agente IA, dashboard empresa o lotes de membresías.</p>
              </div>
            )}
          </div>}
        </div>

        {/* Paso 1: Información de la Empresa */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <h2 className={isModal ? 'text-lg font-bold text-gray-900' : 'text-2xl font-bold text-gray-900 mb-4'}>Información de la Empresa</h2>
            {!isModal && (
            <div className="mb-6 rounded-xl border-2 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-4 shadow-sm">
              <p className="text-base font-medium text-gray-800 leading-relaxed">
                Al ser parte del directorio tendrás <strong>presencia en la plataforma</strong>, <strong>acceso a más clientes</strong>, <strong>validación y credibilidad</strong>, y <strong>herramientas para promocionar tu negocio</strong>.
              </p>
              <p className="mt-2 text-sm font-semibold text-green-800">
                🎁 Membresía 100% gratuita
              </p>
            </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre de la Empresa *</label>
                <input
                  type="text"
                  name="nombre_empresa"
                  value={formData.nombre_empresa}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.nombre_empresa ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ej: Repuestos Chile S.A."
                />
                {errors.nombre_empresa && <p className="text-red-500 text-sm mt-1">{errors.nombre_empresa}</p>}
              </div>

              {!isModal && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">RUT de la Empresa *</label>
                <input
                  type="text"
                  name="rut_empresa"
                  value={formData.rut_empresa}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.rut_empresa ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="50.000.000-1"
                />
                {errors.rut_empresa && <p className="text-red-500 text-sm mt-1">{errors.rut_empresa}</p>}
              </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Región *</label>
                <select
                  name="region"
                  value={formData.region}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.region ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Selecciona una región</option>
                  {regionesDisponibles.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
                {errors.region && <p className="text-red-500 text-sm mt-1">{errors.region}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Comuna *</label>
                <select
                  name="comuna"
                  value={formData.comuna}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.comuna ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={!formData.region}
                >
                  <option value="">{formData.region ? 'Selecciona una comuna' : 'Selecciona primero una región'}</option>
                  {comunasDisponibles.map(comuna => (
                    <option key={comuna} value={comuna}>{comuna}</option>
                  ))}
                </select>
                {errors.comuna && <p className="text-red-500 text-sm mt-1">{errors.comuna}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Dirección del Establecimiento *</label>
                <input
                  type="text"
                  name="direccion_empresa"
                  value={formData.direccion_empresa}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.direccion_empresa ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Dirección completa donde los clientes pueden encontrarte"
                />
                {errors.direccion_empresa && <p className="text-red-500 text-sm mt-1">{errors.direccion_empresa}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono de la Empresa *</label>
                <div className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${errors.telefono_empresa ? 'border-red-500' : 'border-gray-300'}`}>
                  <span className="flex items-center px-4 bg-gray-100 text-gray-600 font-medium border-r border-gray-200">+56</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={9}
                    name="telefono_empresa"
                    value={formData.telefono_empresa}
                    onChange={handleInputChange}
                    placeholder="912345678"
                    className="flex-1 px-4 py-3 border-0 focus:ring-0 min-w-0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Ingresa los 9 dígitos del celular de la empresa (ej: 912345678)</p>
                {errors.telefono_empresa && <p className="text-red-500 text-sm mt-1">{errors.telefono_empresa}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email de la Empresa *</label>
                <input
                  type="email"
                  name="email_empresa"
                  value={formData.email_empresa}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email_empresa ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="contacto@tuempresa.com"
                />
                {formData.selectedOptions?.freeDashboard && (
                  <p className="text-xs text-gray-600 mt-1">
                    Este será el correo con el cual iniciarás sesión en el dashboard. Mantén este correo activo porque aquí recibirás tus credenciales.
                  </p>
                )}
                {errors.email_empresa && <p className="text-red-500 text-sm mt-1">{errors.email_empresa}</p>}
              </div>

              {!isModal && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Página Web (Opcional)</label>
                <input
                  type="url"
                  name="web_actual"
                  value={formData.web_actual}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://www.tuempresa.com (si tienes una)"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Si no tienes página web, ¡no te preocupes! Te ayudamos a crear una en los siguientes pasos.
                </p>
              </div>
              )}

              {!isModal && formData.selectedOptions?.freeDashboard && (
                <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                  <p className="font-semibold mb-2">Si eliges Dashboard Gratis:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>El Email de la Empresa es el correo con el cual iniciarás sesión en el dashboard.</li>
                    <li>Tu RUT y giro serán validados.</li>
                    <li>Un agente se pondrá en contacto para verificar la información.</li>
                  </ul>
                </div>
              )}

              {/* Logo de la empresa */}
              {!isModal && <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Logo de la Empresa (opcional)</label>
                {/* Nota breve: por qué conviene subir el logo */}
                <div
                  className="mb-3 rounded-lg border border-blue-100 bg-blue-50/90 px-4 py-3 text-left shadow-sm"
                  role="note"
                  aria-label="Información sobre la importancia del logo"
                >
                  <p className="text-sm font-semibold text-blue-900 mb-1.5 flex items-center gap-2">
                    <span className="text-base shrink-0" aria-hidden>🏷️</span>
                    Tu logo es parte de tu identidad
                  </p>
                  <p className="text-sm text-blue-800/95 leading-relaxed">
                    Representa la imagen de tu empresa, fortalece tu marca y genera más confianza frente a quienes te descubren en la plataforma. Un perfil con logo se ve más completo, más profesional y es más fácil de reconocer.
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFile}
                  className="block w-full md:w-auto text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {logoPreview && (
                  <div className="mt-3 flex items-center gap-4">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="w-24 h-24 object-contain border rounded-lg bg-white shadow"
                    />
                    <button
                      type="button"
                      onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Quitar logo
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-1">Puedes subir un logo en formato PNG, JPG o SVG.</p>
              </div>}

            </div>
          </div>
        )}

        {/* Paso 2: Información del Representante */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <h2 className={isModal ? 'text-lg font-bold text-gray-900' : 'text-2xl font-bold text-gray-900 mb-6'}>Información del Representante</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombres del Representante *</label>
                <input
                  type="text"
                  name="nombres_representante"
                  value={formData.nombres_representante}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.nombres_representante ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Nombres del propietario o gerente"
                />
                {errors.nombres_representante && <p className="text-red-500 text-sm mt-1">{errors.nombres_representante}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apellidos del Representante *</label>
                <input
                  type="text"
                  name="apellidos_representante"
                  value={formData.apellidos_representante}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.apellidos_representante ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Apellidos del propietario o gerente"
                />
                {errors.apellidos_representante && <p className="text-red-500 text-sm mt-1">{errors.apellidos_representante}</p>}
              </div>
              {!isModal && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">RUT del Representante *</label>
                <input
                  type="text"
                  name="rut_representante"
                  value={formData.rut_representante}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.rut_representante ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="12.345.678-9"
                />
                {errors.rut_representante && <p className="text-red-500 text-sm mt-1">{errors.rut_representante}</p>}
              </div>
              )}
              {!isModal && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Cargo del Representante</label>
                <input
                  type="text"
                  name="cargo_representante"
                  value={formData.cargo_representante}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                  placeholder="Ej: Gerente, Dueño, Administrador"
                />
              </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email del Representante *</label>
                <input
                  type="email"
                  name="email_representante"
                  value={formData.email_representante}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email_representante ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="email@representante.com"
                />
                {errors.email_representante && <p className="text-red-500 text-sm mt-1">{errors.email_representante}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono del Representante *</label>
                <div className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${errors.telefono_representante ? 'border-red-500' : 'border-gray-300'}`}>
                  <span className="flex items-center px-4 bg-gray-100 text-gray-600 font-medium border-r border-gray-200">+56</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={9}
                    name="telefono_representante"
                    value={formData.telefono_representante}
                    onChange={handleInputChange}
                    placeholder="912345678"
                    className="flex-1 px-4 py-3 border-0 focus:ring-0 min-w-0"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Ingresa los 9 dígitos (ej: 912345678)</p>
                {errors.telefono_representante && <p className="text-red-500 text-sm mt-1">{errors.telefono_representante}</p>}
              </div>
              <div className="hidden">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Nacimiento del Representante
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {/* Día */}
                  <div>
                    <select
                      name="dia_nacimiento_rep"
                      value={formData.dia_nacimiento_rep}
                      disabled
                      aria-disabled
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, dia_nacimiento_rep: e.target.value }));
                        const dia = e.target.value;
                        const mes = formData.mes_nacimiento_rep;
                        const año = formData.año_nacimiento_rep;
                        if (dia && mes && año) {
                          const fechaFormateada = `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${año}`;
                          setFormData(prev => ({ ...prev, fecha_nacimiento_representante: fechaFormateada }));
                        } else {
                          setFormData(prev => ({ ...prev, fecha_nacimiento_representante: '' }));
                        }
                        if (errors.fecha_nacimiento_representante) {
                          setErrors(prev => ({ ...prev, fecha_nacimiento_representante: '' }));
                        }
                      }}
                      className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                        errors.fecha_nacimiento_representante ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Día</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(dia => (
                        <option key={dia} value={dia}>{dia}</option>
                      ))}
                    </select>
                  </div>
                  {/* Mes */}
                  <div>
                    <select
                      name="mes_nacimiento_rep"
                      value={formData.mes_nacimiento_rep}
                      disabled
                      aria-disabled
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, mes_nacimiento_rep: e.target.value }));
                        const dia = formData.dia_nacimiento_rep;
                        const mes = e.target.value;
                        const año = formData.año_nacimiento_rep;
                        if (dia && mes && año) {
                          const fechaFormateada = `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${año}`;
                          setFormData(prev => ({ ...prev, fecha_nacimiento_representante: fechaFormateada }));
                        } else {
                          setFormData(prev => ({ ...prev, fecha_nacimiento_representante: '' }));
                        }
                        if (errors.fecha_nacimiento_representante) {
                          setErrors(prev => ({ ...prev, fecha_nacimiento_representante: '' }));
                        }
                      }}
                      className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                        errors.fecha_nacimiento_representante ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Mes</option>
                      <option value="1">Enero</option>
                      <option value="2">Febrero</option>
                      <option value="3">Marzo</option>
                      <option value="4">Abril</option>
                      <option value="5">Mayo</option>
                      <option value="6">Junio</option>
                      <option value="7">Julio</option>
                      <option value="8">Agosto</option>
                      <option value="9">Septiembre</option>
                      <option value="10">Octubre</option>
                      <option value="11">Noviembre</option>
                      <option value="12">Diciembre</option>
                    </select>
                  </div>
                  {/* Año */}
                  <div>
                    <select
                      name="año_nacimiento_rep"
                      value={formData.año_nacimiento_rep}
                      disabled
                      aria-disabled
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, año_nacimiento_rep: e.target.value }));
                        const dia = formData.dia_nacimiento_rep;
                        const mes = formData.mes_nacimiento_rep;
                        const año = e.target.value;
                        if (dia && mes && año) {
                          const fechaFormateada = `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${año}`;
                          setFormData(prev => ({ ...prev, fecha_nacimiento_representante: fechaFormateada }));
                        } else {
                          setFormData(prev => ({ ...prev, fecha_nacimiento_representante: '' }));
                        }
                        if (errors.fecha_nacimiento_representante) {
                          setErrors(prev => ({ ...prev, fecha_nacimiento_representante: '' }));
                        }
                      }}
                      className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                        errors.fecha_nacimiento_representante ? 'border-red-500' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Año</option>
                      {Array.from({ length: new Date().getFullYear() - 1899 }, (_, i) => {
                        const año = new Date().getFullYear() - i;
                        return <option key={año} value={año}>{año}</option>;
                      })}
                    </select>
                  </div>
                </div>
                {false && errors.fecha_nacimiento_representante && (
                  <div className="mt-1 flex items-start gap-2">
                    <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-red-500 text-sm">{errors.fecha_nacimiento_representante}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Paso 3 Modal: Resumen + Términos */}
        {isModal && currentStep === 3 && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Confirmar solicitud</h2>
            <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1.5 text-gray-700">
              <p><span className="font-medium text-gray-900">Empresa:</span> {formData.nombre_empresa}</p>
              <p><span className="font-medium text-gray-900">Email:</span> {formData.email_empresa}</p>
              <p><span className="font-medium text-gray-900">Teléfono:</span> {formData.telefono_empresa ? '+56 ' + formData.telefono_empresa : ''}</p>
              <p><span className="font-medium text-gray-900">Dirección:</span> {formData.direccion_empresa}{formData.comuna ? ', ' + formData.comuna : ''}</p>
              <p><span className="font-medium text-gray-900">Representante:</span> {formData.nombres_representante} {formData.apellidos_representante}</p>
            </div>
            <div className="flex items-start gap-3 pt-2">
              <input
                type="checkbox"
                name="acepta_terminos"
                checked={formData.acepta_terminos}
                onChange={handleInputChange}
                className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <label className="text-sm text-gray-700">
                He leído y acepto los <a href="/terminos/empresas" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Términos y Condiciones para Proveedores/Empresas</a> *
              </label>
            </div>
            {errors.acepta_terminos && <p className="text-red-500 text-sm">{errors.acepta_terminos}</p>}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                name="acepta_notificaciones"
                checked={formData.acepta_notificaciones}
                onChange={handleInputChange}
                className="mt-1 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <label className="text-sm text-gray-700">
                Acepto recibir notificaciones sobre nuevos clientes y novedades de la plataforma
              </label>
            </div>
          </div>
        )}

        {/* Paso 3: Información del Negocio */}
        {!isModal && currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Información del Negocio</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Descripción del Negocio *</label>
                <textarea
                  name="descripcion_negocio"
                  value={formData.descripcion_negocio}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.descripcion_negocio ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Describe brevemente tu negocio, servicios, experiencia, etc."
                  rows={4}
                />
                {errors.descripcion_negocio && <p className="text-red-500 text-sm mt-1">{errors.descripcion_negocio}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Años de Funcionamiento *</label>
                <select
                  name="anos_funcionamiento"
                  value={formData.anos_funcionamiento}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.anos_funcionamiento ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Selecciona una opción</option>
                  <option value="0-1">0 a 1 año</option>
                  <option value="1-3">1 a 3 años</option>
                  <option value="3-5">3 a 5 años</option>
                  <option value="5-10">5 a 10 años</option>
                  <option value="10-20">10 a 20 años</option>
                  <option value=">20">Más de 20 años</option>
                </select>
                {errors.anos_funcionamiento && <p className="text-red-500 text-sm mt-1">{errors.anos_funcionamiento}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Número de Empleados</label>
                <select
                  name="numero_empleados"
                  value={formData.numero_empleados}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                >
                  <option value="">Selecciona una opción</option>
                  <option value="1-5">1 a 5</option>
                  <option value="6-10">6 a 10</option>
                  <option value="11-20">11 a 20</option>
                  <option value="21-50">21 a 50</option>
                  <option value="51-100">51 a 100</option>
                  <option value=">100">Más de 100</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Horario de Atención *</label>
                <div className="overflow-x-auto">
                  <table className="min-w-full border rounded-lg bg-white">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700">Día</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700">Activo</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700">Hora Inicio</th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700">Hora Fin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { key: 'lunes', label: 'Lunes' },
                        { key: 'martes', label: 'Martes' },
                        { key: 'miercoles', label: 'Miércoles' },
                        { key: 'jueves', label: 'Jueves' },
                        { key: 'viernes', label: 'Viernes' },
                        { key: 'sabado', label: 'Sábado' },
                        { key: 'domingo', label: 'Domingo' },
                      ].map(({ key, label }) => (
                        <tr key={key} className="border-t">
                          <td className="px-2 py-2 text-sm text-gray-700">{label}</td>
                          <td className="px-2 py-2">
                            <input
                              type="checkbox"
                              checked={formData.horarios?.[key]?.activo || false}
                              onChange={e => {
                                setFormData(prev => ({
                                  ...prev,
                                  horarios: {
                                    ...prev.horarios,
                                    [key]: {
                                      ...prev.horarios?.[key],
                                      activo: e.target.checked
                                    }
                                  }
                                }));
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="time"
                              value={formData.horarios?.[key]?.inicio || ''}
                              disabled={!formData.horarios?.[key]?.activo}
                              onChange={e => {
                                setFormData(prev => ({
                                  ...prev,
                                  horarios: {
                                    ...prev.horarios,
                                    [key]: {
                                      ...prev.horarios?.[key],
                                      inicio: e.target.value
                                    }
                                  }
                                }));
                              }}
                              className="w-28 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <input
                              type="time"
                              value={formData.horarios?.[key]?.fin || ''}
                              disabled={!formData.horarios?.[key]?.activo}
                              onChange={e => {
                                setFormData(prev => ({
                                  ...prev,
                                  horarios: {
                                    ...prev.horarios,
                                    [key]: {
                                      ...prev.horarios?.[key],
                                      fin: e.target.value
                                    }
                                  }
                                }));
                              }}
                              className="w-28 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">Activa los días y selecciona el horario de atención para cada uno.</p>
                {errors.horario_atencion && <p className="text-red-500 text-sm mt-1">{errors.horario_atencion}</p>}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categorías de Servicios que Ofreces *
                </label>
                {formData.categorias_servicios.length > 0 && (
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Seleccionados: </span>
                    {formData.categorias_servicios.map((cat, i) => (
                      <span key={cat}>
                        {cat}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, categorias_servicios: prev.categorias_servicios.filter(c => c !== cat) }))}
                          className="text-gray-400 hover:text-red-500 ml-0.5 text-xs"
                          aria-label={`Quitar ${cat}`}
                        >×</button>
                        {i < formData.categorias_servicios.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </p>
                )}
                <div className="relative">
                  <input
                    type="text"
                    value={servicioSearch}
                    onChange={(e) => setServicioSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') { setServicioSearch(''); return; }
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = servicioSearch.trim();
                        if (!v || formData.categorias_servicios.includes(v)) { setServicioSearch(''); return; }
                        const isNew = !categoriasDisponibles.includes(v);
                        setFormData(prev => ({
                          ...prev,
                          categorias_servicios: [...prev.categorias_servicios, v],
                          solicitudes_servicio_nueva: isNew ? [...(prev.solicitudes_servicio_nueva || []), v] : (prev.solicitudes_servicio_nueva || [])
                        }));
                        if (errors.categorias_servicios) setErrors(prev => ({ ...prev, categorias_servicios: '' }));
                        setServicioSearch('');
                      }
                    }}
                    placeholder="Buscar o agregar servicio..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    autoComplete="off"
                  />
                  {servicioSearch.trim().length > 0 && (() => {
                    const coinciden = categoriasDisponibles.filter(c =>
                      c.toLowerCase().includes(servicioSearch.toLowerCase()) &&
                      !formData.categorias_servicios.includes(c)
                    );
                    const textoExacto = servicioSearch.trim();
                    const mostrarAgregar = !categoriasDisponibles.some(c => c.toLowerCase() === textoExacto.toLowerCase()) &&
                      !formData.categorias_servicios.includes(textoExacto);
                    const opciones = [...coinciden, ...(mostrarAgregar ? [textoExacto] : [])];
                    if (opciones.length === 0) return null;
                    return (
                      <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {opciones.map((opcion, i) => {
                          const isCustom = !categoriasDisponibles.includes(opcion);
                          return (
                            <li
                              key={opcion}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if (formData.categorias_servicios.includes(opcion)) return;
                                const isNew = !categoriasDisponibles.includes(opcion);
                                setFormData(prev => ({
                                  ...prev,
                                  categorias_servicios: [...prev.categorias_servicios, opcion],
                                  solicitudes_servicio_nueva: isNew ? [...(prev.solicitudes_servicio_nueva || []), opcion] : (prev.solicitudes_servicio_nueva || [])
                                }));
                                if (errors.categorias_servicios) setErrors(prev => ({ ...prev, categorias_servicios: '' }));
                                setServicioSearch('');
                              }}
                              className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 flex items-center justify-between ${i < opciones.length - 1 ? 'border-b border-gray-100' : ''}`}
                            >
                              <span>{opcion}</span>
                              {isCustom && <span className="text-xs text-blue-400">+ Agregar</span>}
                            </li>
                          );
                        })}
                      </ul>
                    );
                  })()}
                </div>
                {errors.categorias_servicios && <p className="text-red-500 text-sm mt-1">{errors.categorias_servicios}</p>}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Marcas de Vehículos que Atiendes *
                </label>
                {formData.marcas_vehiculos.length > 0 && (
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-medium">Seleccionadas: </span>
                    {formData.marcas_vehiculos.map((marca, i) => (
                      <span key={marca}>
                        {marca}
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, marcas_vehiculos: prev.marcas_vehiculos.filter(m => m !== marca) }))}
                          className="text-gray-400 hover:text-red-500 ml-0.5 text-xs"
                          aria-label={`Quitar ${marca}`}
                        >×</button>
                        {i < formData.marcas_vehiculos.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </p>
                )}
                <div className="relative">
                  <input
                    type="text"
                    value={marcaSearch}
                    onChange={(e) => setMarcaSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') { setMarcaSearch(''); return; }
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = marcaSearch.trim();
                        if (!v || formData.marcas_vehiculos.includes(v)) { setMarcaSearch(''); return; }
                        const isNew = !marcasDisponibles.includes(v);
                        setFormData(prev => ({
                          ...prev,
                          marcas_vehiculos: [...prev.marcas_vehiculos, v],
                          solicitudes_marca_nueva: isNew ? [...(prev.solicitudes_marca_nueva || []), v] : (prev.solicitudes_marca_nueva || [])
                        }));
                        if (errors.marcas_vehiculos) setErrors(prev => ({ ...prev, marcas_vehiculos: '' }));
                        setMarcaSearch('');
                      }
                    }}
                    placeholder="Buscar o agregar marca (ej: Toyota, Ford...)..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                    autoComplete="off"
                  />
                  {marcaSearch.trim().length > 0 && (() => {
                    const coinciden = marcasDisponibles.filter(m =>
                      m.toLowerCase().includes(marcaSearch.toLowerCase()) &&
                      !formData.marcas_vehiculos.includes(m)
                    );
                    const textoExacto = marcaSearch.trim();
                    const mostrarAgregar = !marcasDisponibles.some(m => m.toLowerCase() === textoExacto.toLowerCase()) &&
                      !formData.marcas_vehiculos.includes(textoExacto);
                    const opciones = [...coinciden, ...(mostrarAgregar ? [textoExacto] : [])];
                    if (opciones.length === 0) return null;
                    return (
                      <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {opciones.map((opcion, i) => {
                          const isCustom = !marcasDisponibles.includes(opcion);
                          return (
                            <li
                              key={opcion}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                if (formData.marcas_vehiculos.includes(opcion)) return;
                                const isNew = !marcasDisponibles.includes(opcion);
                                setFormData(prev => ({
                                  ...prev,
                                  marcas_vehiculos: [...prev.marcas_vehiculos, opcion],
                                  solicitudes_marca_nueva: isNew ? [...(prev.solicitudes_marca_nueva || []), opcion] : (prev.solicitudes_marca_nueva || [])
                                }));
                                if (errors.marcas_vehiculos) setErrors(prev => ({ ...prev, marcas_vehiculos: '' }));
                                setMarcaSearch('');
                              }}
                              className={`px-3 py-2 text-sm cursor-pointer hover:bg-green-50 flex items-center justify-between ${i < opciones.length - 1 ? 'border-b border-gray-100' : ''}`}
                            >
                              <span>{opcion}</span>
                              {isCustom && <span className="text-xs text-green-400">+ Agregar</span>}
                            </li>
                          );
                        })}
                      </ul>
                    );
                  })()}
                </div>
                {errors.marcas_vehiculos && <p className="text-red-500 text-sm mt-1">{errors.marcas_vehiculos}</p>}
              </div>

              {/* Recordatorio: lo agregado sin estar en listado se considera solicitud para catálogo */}
              {(formData.solicitudes_servicio_nueva?.length > 0 || formData.solicitudes_marca_nueva?.length > 0) && (
                <div className="md:col-span-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Las marcas o servicios que agregaste y no estaban en el listado se enviarán también como solicitud para incorporarlos al catálogo de la página.
                </div>
              )}
            </div>
            
            {/* Términos y condiciones - Solo para agente (cuando isAgente = true) */}
            {isAgente && (
              <div className="mt-8 space-y-4 border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Términos y Condiciones</h3>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      name="acepta_terminos"
                      checked={formData.acepta_terminos}
                      onChange={handleInputChange}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label className="text-sm text-gray-700">
                      He leído y acepto los <a href="/terminos/empresas" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Términos y Condiciones para Proveedores/Empresas</a> *
                    </label>
                  </div>
                  {errors.acepta_terminos && <p className="text-red-500 text-sm">{errors.acepta_terminos}</p>}
                  
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      name="acepta_notificaciones"
                      checked={formData.acepta_notificaciones}
                      onChange={handleInputChange}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label className="text-sm text-gray-700">
                      Acepto recibir notificaciones sobre nuevos clientes, promociones y novedades de la plataforma
                    </label>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      name="acepta_visita_campo"
                      checked={formData.acepta_visita_campo}
                      onChange={handleInputChange}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label className="text-sm text-gray-700">
                      Acepto que un agente de campo visite mi establecimiento para verificar la información (requerido para aprobar la solicitud)
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Paso 4: Servicios Web - Solo cuando eligió "ayuda con página web" */}
        {currentStep === 4 && !isAgente && showWebsiteStep && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Ayuda con tu Página Web</h2>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-blue-900 mb-3">🌐 ¡Te ayudamos a tener presencia digital!</h3>
              <p className="text-blue-800 mb-4">
                Ofrecemos servicios de desarrollo web especializados para empresas del sector automotriz:
                alternativas, pagos flexibles, tiempos y forma de trabajo.
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-700">
                <div>
                  <p>✅ Diseño profesional y moderno</p>
                  <p>✅ Optimizado para móviles</p>
                  <p>✅ Integración con redes sociales</p>
                </div>
                <div>
                  <p>✅ SEO para aparecer en Google</p>
                  <p>✅ Sistema de citas online</p>
                  <p>✅ Hosting y dominio incluidos</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="font-semibold text-blue-900 mb-2">Detalles de pago</p>
                <p className="text-blue-800">3 pagos: 40%, 30%, 30% según avance.</p>
                <p className="font-semibold text-blue-900 mt-2 mb-2">Proceso</p>
                <p className="text-blue-800">Desarrollo, carga de información, puesta en marcha y post puesta en marcha.</p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Elige el plan que mejor se adapte a tu negocio</h3>

              {true && (
                <div className="space-y-4 ml-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Elige el plan que mejor se adapte a tu negocio:</h3>
                  
                  <div className="grid gap-6">
                    {opcionesPaginaWeb.map(opcion => (
                      <div key={opcion.tipo} className="border border-gray-200 rounded-lg p-6 hover:border-blue-300 transition-colors">
                        <label className="flex items-start space-x-3 cursor-pointer">
                          <input
                            type="radio"
                            name="tipo_pagina_web"
                            value={opcion.tipo}
                            checked={formData.tipo_pagina_web === opcion.tipo}
                            onChange={handleInputChange}
                            className="mt-1 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="text-lg font-semibold text-gray-900">{opcion.nombre}</h4>
                              <span className="text-2xl font-bold text-blue-600">{opcion.precio}</span>
                            </div>
                            <p className="text-gray-600 mb-3">{opcion.descripcion}</p>
                            <div className="grid md:grid-cols-2 gap-1">
                              {opcion.incluye.map((item, index) => (
                                <p key={index} className="text-sm text-gray-700">✓ {item}</p>
                              ))}
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                  {errors.tipo_pagina_web && <p className="text-red-500 text-sm mt-1">{errors.tipo_pagina_web}</p>}
                </div>
              )}

              {/* Redes Sociales */}
              <div className="mt-8 space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="tiene_redes_sociales"
                    checked={formData.tiene_redes_sociales}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="text-lg font-medium text-gray-700">
                    Tengo redes sociales para mi negocio
                  </label>
                </div>

                {formData.tiene_redes_sociales && (
                  <div className="ml-6 grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
                      <input
                        type="url"
                        name="redes_sociales.facebook"
                        value={formData.redes_sociales.facebook}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://facebook.com/tu-negocio"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Instagram</label>
                      <input
                        type="url"
                        name="redes_sociales.instagram"
                        value={formData.redes_sociales.instagram}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://instagram.com/tu-negocio"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Business</label>
                      <div className="flex border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                        <span className="flex items-center px-3 bg-gray-100 text-gray-600 font-medium border-r border-gray-200">+56</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={9}
                          name="redes_sociales.whatsapp"
                          value={formData.redes_sociales.whatsapp || ''}
                          onChange={handleInputChange}
                          className="flex-1 px-3 py-2 border-0 focus:ring-0 min-w-0"
                          placeholder="912345678"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">9 dígitos del celular (ej: 912345678)</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">TikTok</label>
                      <input
                        type="url"
                        name="redes_sociales.tiktok"
                        value={formData.redes_sociales.tiktok}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="https://tiktok.com/@tu-negocio"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Paso 5: Confirmación y Términos - Solo para empresas creadas por cliente, no por agente */}
        {currentStep === 5 && !isAgente && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Confirmación y Términos</h2>
            

            {/* Resumen de información */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-lg mb-4">Resumen de tu solicitud:</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><span className="font-medium">Empresa:</span> {formData.nombre_empresa}</p>
                  <p><span className="font-medium">RUT:</span> {formData.rut_empresa}</p>
                  <p><span className="font-medium">Representante:</span> {formData.nombres_representante} {formData.apellidos_representante}</p>
                  <p><span className="font-medium">Email empresa:</span> {formData.email_empresa}</p>
                  <p><span className="font-medium">Teléfono:</span> {formData.telefono_empresa.length === 9 ? '+56' + formData.telefono_empresa : formData.telefono_empresa}</p>
                </div>
                <div>
                  <p><span className="font-medium">Dirección:</span> {formData.direccion_empresa}</p>
                  <p><span className="font-medium">Comuna:</span> {formData.comuna}</p>
                  <p><span className="font-medium">Región:</span> {formData.region}</p>
                  <p><span className="font-medium">Servicios:</span> {formData.categorias_servicios.length} categorías</p>
                  {(formData.solicitudes_servicio_nueva?.length > 0 || formData.solicitudes_marca_nueva?.length > 0) && (
                    <p><span className="font-medium">Solicitudes para catálogo:</span> {[formData.solicitudes_servicio_nueva?.length ? `${formData.solicitudes_servicio_nueva.length} servicio(s) nuevo(s)` : null, formData.solicitudes_marca_nueva?.length ? `${formData.solicitudes_marca_nueva.length} marca(s) nueva(s)` : null].filter(Boolean).join(', ')}</p>
                  )}
                  {(formData.necesita_pagina_web || formData.selectedOptions?.websiteHelp) && (
                    <p><span className="font-medium">Página web:</span> {formData.tipo_pagina_web || 'Sí (plan por definir)'}</p>
                  )}
                  {formData.selectedOptions?.membershipLots && (
                    <p><span className="font-medium">Lotes Premium:</span> {formData.membershipLot ? `Lote ${formData.membershipLot}` : 'Por definir'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Términos y condiciones */}
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="acepta_terminos"
                  checked={formData.acepta_terminos}
                  onChange={handleInputChange}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="text-sm text-gray-700">
                  He leído y acepto los <a href="/terminos/empresas" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Términos y Condiciones para Proveedores/Empresas</a> *
                </label>
              </div>
              {errors.acepta_terminos && <p className="text-red-500 text-sm">{errors.acepta_terminos}</p>}

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="acepta_notificaciones"
                  checked={formData.acepta_notificaciones}
                  onChange={handleInputChange}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="text-sm text-gray-700">
                  Acepto recibir notificaciones sobre nuevos clientes, promociones y novedades de la plataforma
                </label>
              </div>

              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="acepta_visita_campo"
                  checked={formData.acepta_visita_campo}
                  onChange={handleInputChange}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="text-sm text-gray-700">
                  Acepto que un agente de campo visite mi establecimiento para verificar la información (requerido para aprobar la solicitud)
                </label>
              </div>
            </div>

            {/* Información del proceso */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-5">
              <h4 className="font-semibold text-green-900 mb-3">¿Qué sigue después?</h4>
              <ol className="text-sm text-green-800 space-y-3 list-decimal list-inside">
                <li>Revisaremos tu solicitud en 48-72 horas.</li>
                <li>Te contactaremos para validar la información.</li>
                <li>
                  Recibirás un email de confirmación y un WhatsApp para que revises tu email.
                  <ul className="mt-2 ml-4 space-y-1 list-disc list-inside text-green-700">
                    <li>Se activa tu tarjeta de empresa en el HomePage, con un perfil, logo e información de tu negocio para que los clientes te encuentren. (Sin costo)</li>
                    {formData.selectedOptions?.freeDashboard && (
                      <li>Si seleccionaste que quieres un dashboard, te crearemos un acceso con el mail de la empresa (el informado en el paso 1). Recibirás credenciales iniciales y luego podrás cambiar tu contraseña. El dashboard es gratis, con opción de cambiar a membresía Premium o Enterprise.</li>
                    )}
                  </ul>
                </li>
                {formData.selectedOptions?.websiteHelp && (
                  <li>
                    Te enviaremos el presupuesto detallado para tu página web:
                    <ul className="mt-2 ml-4 space-y-1 list-disc list-inside text-green-700">
                      <li>Proceso de pago: 3 pagos (40%, 30%, 30%) según avance.</li>
                      <li>Proceso: desarrollo, carga de información, puesta en marcha y post puesta en marcha.</li>
                    </ul>
                  </li>
                )}
                {formData.selectedOptions?.membershipLots && (
                  <li>Te enviaremos una propuesta comercial para lotes Premium Conductor con el volumen indicado.</li>
                )}
              </ol>
            </div>
          </div>
        )}

        {/* Navegación */}
        <div className={`flex justify-between ${isModal ? 'mt-4' : 'mt-8'}`}>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevStep}
              className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-400 transition-colors"
            >
              ← Anterior
            </button>
          )}
          <div className="flex-1"></div>
          
          {/* Indicador de progreso */}
          <div className="flex-1 flex justify-center items-center">
            <div className="flex space-x-2">
              {Array.from({ length: maxStep }, (_, i) => i + 1).map(step => (
                <div
                  key={step}
                  className={`w-3 h-3 rounded-full ${
                    step <= stepDisplay ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="ml-3 text-sm text-gray-600">
              Paso {stepDisplay} de {maxStep}
            </span>
          </div>

          <div className="flex-1 flex justify-end">
            {currentStep < maxStep ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Siguiente →
              </button>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Enviando solicitud...' : 'Enviar solicitud'}
              </button>
            )}
          </div>
        </div>
      </form>
      )}
      </div>
    </div>
  );
}

export default function RegistroProveedor({ isModal = false, onClose, searchOverride = null } = {}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [perfilProveedor, setPerfilProveedor] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar si el usuario ya tiene un perfil de proveedor o solicitud
  useEffect(() => {
    const checkExistingProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Verificar perfil existente
        const perfilQuery = query(
          collection(db, 'empresas'),
          where('uid', '==', user.uid)
        );
        const perfilSnapshot = await getDocs(perfilQuery);
        
        if (!perfilSnapshot.empty) {
          setPerfilProveedor(perfilSnapshot.docs[0].data());
          setLoading(false);
          return;
        }

        // Verificar solicitud pendiente
        const solicitudQuery = query(
          collection(db, 'solicitudes_empresa'),
          where('email_empresa', '==', user.email)
        );
        const solicitudSnapshot = await getDocs(solicitudQuery);
        
        if (!solicitudSnapshot.empty) {
          const solicitudData = solicitudSnapshot.docs[0].data();
          setPerfilProveedor({ ...solicitudData, tipo: 'solicitud_pendiente' });
        }
      } catch (error) {
        console.error('Error checking existing profile:', error);
      } finally {
        setLoading(false);
      }
    };

    checkExistingProfile();
  }, [user]);

  if (isModal) {
    if (loading) {
      return (
        <div className="py-12 flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mb-3" />
          <p className="text-gray-500 text-sm">Verificando...</p>
        </div>
      );
    }
    if (perfilProveedor) {
      return (
        <div className="px-6 py-8 text-center">
          {perfilProveedor.tipo === 'solicitud_pendiente' ? (
            <>
              <div className="text-5xl mb-3">⏳</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{LAUNCH.solicitudEmpresaEnRevisionTitulo}</h2>
              <p className="text-gray-600 mb-4">{LAUNCH.solicitudEmpresaEnRevisionTexto}</p>
              <button onClick={onClose} className="bg-blue-600 text-white px-6 py-2 rounded-xl hover:bg-blue-700 transition-colors text-sm font-bold">Cerrar</button>
            </>
          ) : (
            <>
              <div className="text-5xl mb-3">✅</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">¡Ya eres un Proveedor!</h2>
              <p className="text-gray-600 mb-4">Tu empresa <strong>{perfilProveedor.nombre}</strong> ya está registrada.</p>
              <button onClick={() => { navigate('/dashboard/proveedor'); onClose?.(); }} className="bg-emerald-600 text-white px-6 py-2 rounded-xl hover:bg-emerald-700 transition-colors text-sm font-bold">{LAUNCH.ctaMiCuenta}</button>
            </>
          )}
        </div>
      );
    }
    return (
      <div className="px-4 py-4">
        <RegistroProveedorSinAuth searchOverride={searchOverride} isModal={true} onClose={onClose} />
      </div>
    );
  }

  if (!user) {
    // Usuario no autenticado - mostrar formulario de registro sin auth
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="container mx-auto">
          <RegistroProveedorSinAuth />
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando información del proveedor...</p>
        </div>
      </div>
    );
  }

  if (perfilProveedor) {
    if (perfilProveedor.tipo === 'solicitud_pendiente') {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="text-6xl mb-4">⏳</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {LAUNCH.solicitudEmpresaEnRevisionTitulo}
            </h2>
            <p className="text-gray-600 mb-4">
              {LAUNCH.solicitudEmpresaEnRevisionTexto}
            </p>
            <div className="space-y-2 text-sm text-gray-500">
              <p>Estado: <span className="font-medium">{perfilProveedor.estado}</span></p>
              <p>Etapa: <span className="font-medium">{perfilProveedor.etapa_actual}</span></p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Volver al Inicio
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 px-4">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Ya eres un Proveedor!
            </h2>
            <p className="text-gray-600 mb-4">
              Tu empresa <strong>{perfilProveedor.nombre}</strong> ya está registrada como proveedor en nuestra plataforma.<br />
            </p>
            <button
              onClick={() => navigate('/dashboard/proveedor')}
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              {LAUNCH.ctaMiCuenta}
            </button>
          </div>
        </div>
      );
    }
  }

  // Usuario autenticado pero sin perfil - mostrar formulario normal
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="container mx-auto">
        <RegistroProveedorSinAuth />
      </div>
    </div>
  );
}

// Exportar RegistroProveedorSinAuth para uso en otros componentes
export { RegistroProveedorSinAuth };
